import { html } from "lit";
import { formatHtml } from "@trumpf/ux.common-storybook";
import { ifDefined } from "lit/directives/if-defined.js";
import { countryList } from "../../../components/location-selection/country-list";
import { baiduMapsClientKey } from "../../../utils";

export default {
    title: "Website/Partial Templates/Location Map",
    parameters: {
        docs: {
            source: {
                transform: formatHtml,
            },
        },
    },
    component: "ux-iws-location-map",
    args: {
        clientKey: "gme-trumpfgmbhcokg",
        formSelector: undefined,
        noResultsCodeSelector: undefined,
        routeLinkSelector: undefined,
        type: "location-details",
        isSourceCode: false,
        useLegacyLocationSelection: false,
        withoutMap: false,
    },
    argTypes: {
        clientKey: { control: "text" },
        formSelector: { control: "text" },
        noResultsCodeSelector: { control: "text" },
        routeLinkSelector: { control: "text" },
        type: {
            control: "radio",
            options: ["country-locations", "dealer-search", "location-details"],
        },
        useLegacyLocationSelection: { control: "boolean" },
        withoutMap: { control: "boolean" },
    },
};

const getConfigJsonString = ({
    mockJsonNumber,
}: {
    mockJsonNumber: string;
}) => `
    { "fetchUrl":
    "./mock-data/location-map/dealer-search-${mockJsonNumber}.json",
    "logo_icon":
    "https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/location-detail-marker-small.png",
    "gray_icon":
    "https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Vertretung_Iconx2.png",
    "blue_icon":
    "https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Tochtergesellschaft_Iconx2.png",
    "green_icon":
    "https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Stammsitz_Iconx2.png",
    "globe_icon":
    "https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Globe_Icon.png",
    "gray_icon_hover":
    "https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/location-dot-gray_hover.jpg",
    "blue_icon_hover":
    "https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/location-dot-blue_hover.jpg",
    "green_icon_hover":
    "https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/location-dot-green_hover.jpg",
    "globe_icon_hover":
    "https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Globe_Icon_hover.png",
    "logoPath":
    "https://trumpf.com.cms.qa.tru/uploads/tx_trumpflocations/logos/",
    "phoneText": "Telefon", "faxText": "Telefax", "emailText": "E-Mail" }
`;
// higher probability for the higher numbers
const mockJsonNumbers = ["1", "2", "3", "4", "2", "3", "4", "3", "4", "4"];

const DealerSearchTemplate = ({
    clientKey,
    type,
    routeLinkSelector,
    noResultsCodeSelector,
    formSelector,
    mapProvider,
}) => {
    return html`
        <div class="ux-row">
            <div class="ux-col ux-u-my-m">
                <h2
                    class="ux-text-headline ux-text-headline--big ux-u-text-align--center"
                >
                    Power by the Hour in Zahlen
                </h2>
            </div>
        </div>
        <div class="row">
            <div class="gr-4 push-4 gr-8@t push-2@t gr-12@m push-0@m">
                <form action="#" id="dealer-search-form">
                    <ux-input-field
                        name="postcode"
                        label="postcode"
                        type="text"
                    ></ux-input-field>
                    <div class="ux-u-margin-top-10">
                        <ux-checkbox
                            name="show-all"
                            label="show all dealers"
                        ></ux-checkbox>
                    </div>
                    <div class="ux-u-margin-top-15 ux-u-text-align--center">
                        <button
                            type="submit"
                            class="ux-button ux-button--green"
                        >
                            Search
                        </button>
                    </div>
                </form>
            </div>
        </div>

        <div class="row">
            <div class="gr-10 push-1">
                <div class="ux-iws-location-map__legend ux-text-tiny">
                    <div class="ux-iws-location-map__legend-item">
                        <span
                            class="ux-iws-location-map__legend-image"
                            style="background-image: url(https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Stammsitz_Iconx2.png);"
                        ></span>
                        <span>Some text</span>
                    </div>
                    <div class="ux-iws-location-map__legend-item">
                        <span
                            class="ux-iws-location-map__legend-image"
                            style="background-image: url(https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Tochtergesellschaft_Iconx2.png);"
                        ></span>
                        <span>Some other text</span>
                    </div>
                    <div class="ux-iws-location-map__legend-item">
                        <span
                            class="ux-iws-location-map__legend-image"
                            style="background-image: url(https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Vertretung_Iconx2.png);"
                        ></span>
                        <span>Some other text 2</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="gr-12 ux-u-text-align--center ux-u-margin-bottom-20">
                <div class="ux-color--danger">
                    Es wurde kein Händler gefunden für die PLZ
                    <span class="ux-iws-location-map__no-results-code"></span>
                </div>
            </div>
        </div>

        <div class="row">
            <div
                class="gr-10 push-1 gr-12@m push-0@m ux-u-margin-bottom-50 ux-u-margin-xl-bottom-60"
            >
                <ux-iws-location-map
                    client-key="${ifDefined(clientKey)}"
                    map-provider="${mapProvider}"
                    type="${type}"
                    route-link-selector="${ifDefined(routeLinkSelector)}"
                    form-selector="${ifDefined(formSelector)}"
                    no-results-code-selector="${ifDefined(
                        noResultsCodeSelector
                    )}"
                >
                    <script
                        type="application/json"
                        slot="config"
                        .innerHTML=${getConfigJsonString({
                            mockJsonNumber:
                                mockJsonNumbers[
                                    Math.floor(
                                        Math.random() * mockJsonNumbers.length
                                    )
                                ],
                        })}
                    ></script>
                    <div slot="cookie-notice">${cookieText()}</div>
                </ux-iws-location-map>
            </div>
        </div>
    `;
};

const CountryLocationsTemplate = ({
    clientKey,
    type,
    routeLinkSelector,
    noResultsCodeSelector,
    formSelector,
    mapProvider,
    withoutMap,
    useLegacyLocationSelection,
}) => {
    return html`
        <div class="row">
            <div class="gr-10 push-1 gr-12@m push-0@m ux-u-margin-vertical-20">
                ${useLegacyLocationSelection
                    ? legacyLocationSelection()
                    : locationSelection()}
            </div>
        </div>

        <div class="row">
            <div class="gr-10 push-1 gr-12@m push-0@m ux-u-margin-top-15">
                <!-- if country lead -->
                <div
                    class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase"
                >
                    Contacts for germany
                </div>
                <div class="ux-u-margin-top-20">
                    <div class="ux-text-tiny ux-text-tiny--bold">
                        TRUMPF SE + Co. KG
                    </div>
                    <span
                        style="display: inline-block;"
                        class="ux-text-tiny ux-u-margin-right-30"
                    >
                        Johann-Maus-Str. 2 <br />
                        71254 Ditzingen <br />
                        Deutschland
                    </span>
                    <span style="display: inline-block;" class="ux-text-tiny">
                        <a href="tel:+39-02-484891">Telefon +39 02 484891</a>
                        <br />
                        Fax +39 02 4848 9500
                        <br />
                        <a
                            href='mailto:info@it.trumpf.com?subject=Anfrage über Website: "Standorte"&amp;body=%0D%0ADiese Anfrage kam über die Seite https://www.trumpf.com/de_INT/unternehmen/trumpf-gruppe/standorte/country/italy/'
                            >info@it.trumpf.com</a
                        >
                    </span>
                </div>
            </div>
        </div>

        ${withoutMap
            ? ""
            : html`<div class="row">
                      <div
                          class="gr-10 push-1 gr-12@m push-0@m ux-u-margin-bottom-20"
                      >
                          <div class="ux-iws-location-map__legend ux-text-tiny">
                              <div class="ux-iws-location-map__legend-item">
                                  <span
                                      class="ux-iws-location-map__legend-image"
                                      style="background-image: url(https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Stammsitz_Iconx2.png);"
                                  ></span>
                                  <span> Legend text </span>
                              </div>
                              <div class="ux-iws-location-map__legend-item">
                                  <span
                                      class="ux-iws-location-map__legend-image"
                                      style="background-image: url(https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Tochtergesellschaft_Iconx2.png);"
                                  ></span>
                                  <span>Some other text</span>
                              </div>
                              <div class="ux-iws-location-map__legend-item">
                                  <span
                                      class="ux-iws-location-map__legend-image"
                                      style="background-image: url(https://www.trumpf.com/_assets/c6f81dd72b876bbc6ea1c443ba1feb37/Images/assets/Vertretung_Iconx2.png);"
                                  ></span>
                                  <span>Some other text 2</span>
                              </div>
                              <div class="ux-iws-location-map__legend-item">
                                  <span
                                      class="ux-iws-location-map__legend-image"
                                      style="background-image: url(https://trumpf.com.cms.qa.tru//typo3conf/ext/trumpf_package/Resources/Public/Images/assets/Globe_Icon.png);"
                                  ></span>
                                  <span>Some other text 3</span>
                              </div>
                          </div>
                      </div>
                  </div>

                  <div class="row">
                      <div
                          class="gr-10 push-1 gr-12@m push-0@m ux-u-margin-bottom-50 ux-u-margin-xl-bottom-60"
                      >
                          <ux-iws-location-map
                              client-key="${ifDefined(clientKey)}"
                              type="${type}"
                              route-link-selector="${ifDefined(
                                  routeLinkSelector
                              )}"
                              map-provider="${mapProvider}"
                              form-selector="${ifDefined(formSelector)}"
                              no-results-code-selector="${ifDefined(
                                  noResultsCodeSelector
                              )}"
                          >
                              <script
                                  type="application/json"
                                  slot="config"
                                  .innerHTML=${getConfigJsonString({
                                      mockJsonNumber: "3",
                                  })}
                              ></script>
                              <div slot="cookie-notice">${cookieText()}</div>
                              <div slot="privacy-notice">${privacyText()}</div>
                          </ux-iws-location-map>
                      </div>
                  </div>`}
    `;
};

const locationSelection = () => {
    return html`
        <ux-iws-location-selection
            top-label="Locations in"
            country-name="Germany"
            button-label="Change country"
        >
            ${countryList(false)}
        </ux-iws-location-selection>
    `;
};

const legacyLocationSelection = () => {
    return html`
        <div class="ux-text-tiny ux-text-tiny--bold ux-u-text-uppercase">
            locations in
        </div>
        <ux-accordion
            active-title-class="--"
            class="ux-iws-location-map__selection-accordion"
        >
            <div slot="title">
                <h2 class="headline-big">Germany</h2>
                <button type="button" class="ux-button ux-u-margin-left-30">
                    <ux-icon
                        class="ux-u-margin-right-5 ux-color-success"
                        size-s="23"
                        name="globe-coordinates"
                        weight="light"
                    ></ux-icon>
                    Change country
                </button>
            </div>
            ${countryList(true)}
        </ux-accordion>
    `;
};

const cookieText = () => {
    return html`
        <ux-icon name="circle-info" weight="light" size-s="60"></ux-icon>
        <div
            class="ux-text-headline ux-text-headline--medium ux-u-margin-top-20"
        >
            You have not accepted the cookies
        </div>
        <div class="ux-text-normal ux-text-normal--light-gray">
            <p>
                Accept them now!
                <a href="#" data-ux-cookie-layer="settings"
                    >Open Cookie settings</a
                >
            </p>
        </div>
    `;
};

const privacyText = () => {
    return html`
        <ux-icon name="circle-info" weight="light" size-s="60"></ux-icon>
        <div
            class="ux-text-headline ux-text-headline--medium ux-u-margin-top-20"
        >
            You have not accepted the privacy settings
        </div>
        <div class="ux-text-normal ux-text-normal--light-gray">
            <p>
                Accept them now!
                <a href="#" data-ux-privacy-layer="settings"
                    >Open privacy settings</a
                >
            </p>
        </div>
    `;
};

export const DealerSearch = DealerSearchTemplate.bind({});
DealerSearch.args = {
    type: "dealer-search",
    formSelector: "#dealer-search-form",
    noResultsCodeSelector: ".ux-iws-location-map__no-results-code",
    mapProvider: "google",
    clientKey: "gme-trumpfgmbhcokg",
};
DealerSearch.argTypes = {
    type: {
        control: "radio",
        options: ["country-locations", "dealer-search", "location-details"],
    },
};

export const DealerSearchBaidu = DealerSearchTemplate.bind({});
DealerSearchBaidu.args = {
    type: "dealer-search",
    formSelector: "#dealer-search-form",
    noResultsCodeSelector: ".ux-iws-location-map__no-results-code",
    mapProvider: "baidu",
    clientKey: baiduMapsClientKey,
};

export const CountryLocations = CountryLocationsTemplate.bind({});
CountryLocations.args = {
    type: "country-locations",
    mapProvider: "google",
    clientKey: "gme-trumpfgmbhcokg",
};
CountryLocations.argTypes = {
    type: {
        control: "radio",
        options: ["country-locations", "dealer-search", "location-details"],
    },
};

export const CountryLocationsBaidu = CountryLocationsTemplate.bind({});
CountryLocationsBaidu.args = {
    type: "country-locations",
    mapProvider: "baidu",
    clientKey: baiduMapsClientKey,
};
CountryLocationsBaidu.argTypes = {
    type: {
        control: "radio",
        options: ["country-locations", "dealer-search", "location-details"],
    },
};
